import type { PageKeys } from 'consts/api';
import { translate } from 'helpers/lang.helpers';
import { loadState } from 'helpers/localStorage';
import { createContext, useEffect, useMemo, useReducer } from 'react';
import { buildHttp } from 'services/http';
import { translationsAction } from 'store/actions/language.actions';
import type { Payload } from 'store/state';
import { buildAppState, reducer } from 'store/state';
import type { AppConfig } from 'types';

export const ApplicationContext = createContext({} as ReturnType<typeof BuildApplicationContext>);

export type Translate = (key: string, page?: PageKeys, exact?: boolean) => string;

const INITIAL_STATE = loadState(buildAppState());

export function BuildApplicationContext(config: AppConfig) {
	const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
	const { data, user } = state;
	const { language } = state.language;
	const http = useMemo(() => buildHttp(user.token, dispatch), [user.token]);

	const t = (key = '', page: PageKeys = 'general', exact = false): string => {
		return translate(key, page, exact, language, state.language.translations);
	};

	const dataSelector = <T>(url: string, defaults: T) => {
		return undefined === data[url] ? defaults : (data[url] as T);
	};

	useEffect(() => {
		const url = config.API_CONTENT_URL || config.API.external.contentApi;
		const lang = language.toLowerCase();

		const promises = Object.keys(config.API.content).map(page => {
			const listguid = config.API.content[page as PageKeys];
			const payload = { listguid, language: lang };
			return http.post(url, payload, undefined, {});
		});

		Promise.all(promises).then(results => {
			const translations: Partial<Record<PageKeys, Record<string, string>>> = {};
			for (const item of results) {
				const { language, ...pageTranslations } = item;
				for (const key in pageTranslations) {
					translations[key as PageKeys] = pageTranslations[key];
				}
			}
			dispatch([
				translationsAction,
				{
					language: {
						language,
						translations
					}
				} as Payload
			]);
		});
	}, [language, config]); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		config,
		dataSelector,
		dispatch,
		http,
		state,
		t
	};
}
